import { parsePwaEntryParams } from '@core/links';

/**
 * Входящие ссылки PWA: приглашение друга (<slug>?ref=<код>) и подарок (<slug>?gift=<код>) — пара к ссылкам
 * «поделиться» из PWA (@core/links). Гость по такой ссылке обычно ещё не вошёл: гвард уводит его на /login,
 * и query теряется. Поэтому коды запоминаются при старте — отдельно для каждого заведения — и используются
 * позже: код приглашения — при регистрации по телефону, код подарка — сразу после входа.
 */

const refKey = (slug: string) => `pwa:ref:${slug}`;
const giftKey = (slug: string) => `pwa:gift:${slug}`;

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value === null) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, value);
    }
  } catch {
    // localStorage недоступен (приватный режим) — приглашение/подарок по ссылке просто не сработают
  }
}

/** Запомнить ?ref= и ?gift= текущего адреса для заведения slug и убрать их из адресной строки. */
export function captureEntryParams(slug: string, location: Location = window.location): void {
  if (!slug || !location.search) return;
  const { referralCode, giftClaimCode } = parsePwaEntryParams(location.search);
  if (referralCode) write(refKey(slug), referralCode);
  if (giftClaimCode) write(giftKey(slug), giftClaimCode);

  if (referralCode || giftClaimCode) {
    // Убираем коды из адреса: обновление страницы или «добавить на экран» не должны повторять их.
    const params = new URLSearchParams(location.search);
    params.delete('ref');
    params.delete('gift');
    const query = params.toString();
    try {
      window.history.replaceState(window.history.state, '', `${location.pathname}${query ? `?${query}` : ''}${location.hash}`);
    } catch {
      // не критично
    }
  }
}

/** Код приглашения для регистрации в заведении slug (если клиент пришёл по ссылке друга). */
export function pendingReferralCode(slug: string): string | undefined {
  return read(refKey(slug)) ?? undefined;
}

/** Приглашение использовано (регистрация прошла) — больше не передаём. */
export function clearReferralCode(slug: string): void {
  write(refKey(slug), null);
}

/** Код подарка для заведения slug — забирается один раз (чтение удаляет его). */
export function takeGiftClaimCode(slug: string): string | null {
  const code = read(giftKey(slug));
  if (code) write(giftKey(slug), null);
  return code;
}
